import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { styled, useTheme } from '@mui/material/styles';
import { Button, Grid, Stack, Typography, Box } from '@mui/material';
import { Country, State } from 'country-state-city';
import { useNavigate } from 'react-router-dom';

import MainCard from 'components/MainCard';
import CompanyInfo from './components/CompanyInfo';
import Playbook from './components/Playbook';
import Theme from './components/Theme';
import { useParams } from 'react-router-dom';
import { apiRequest } from 'helpers/apiRequests';
import { toast } from 'react-toastify';
import NotFound from 'components/NotFound';
import { LoadingButton } from '@mui/lab';
import useUnsavedChangesWarning from 'hooks/useUnsavedChangesWarning';
import usePrompt from 'hooks/usePrompt';

const CustomTypography = styled(Typography)(({ theme, width }) => ({
  position: 'relative',
  '&:after': {
    content: "''",
    width: `calc(100% - ${width}px)`,
    position: 'absolute',
    right: '0',
    bottom: '8px',
    borderBottom: '1px dashed #D9D9D9'
  }
}));

const GroupCompanyEdit = () => {
  const theme = useTheme();
  const [isDirty, setIsDirty] = useUnsavedChangesWarning();
  const params = useParams();
  const navigate = useNavigate();
  const [primary, setPrimary] = useState('#0000');
  const [secondary, setSecondary] = useState('#0000');
  const [logo, setLogo] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [states, setStates] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupCompanies, setGroupCompanies] = useState([]);

  const validationSchema = Yup.object().shape({
    company_name: Yup.string().required('Company Name is required.'),
    web_url: Yup.string().url('Invalid URL.').required('Company Website is required.'),
    unique_url_component: Yup.string().required('Company Unique URL is required.').matches(/^\S*$/, 'Space is not allowed.'),
    fiscal_start_month: Yup.number()
      .typeError('Company fiscal year period is required.')
      .required('Company fiscal year period is required.')
      .nullable(),
    // update_frequency: Yup.string().required('Update frequency is required.'),
    // total_users: Yup.number().required('Number of users is required.').min(1, 'Number of users must be at least 1.'),
    // branches: Yup.array()
    //   .of(
    //     Yup.object().shape({
    //       branch_name: Yup.string().required('Branch Name is required.'),
    //       branch_country: Yup.string().required('Country is required.'),
    //       branch_city: Yup.string().required('City is required.'),
    //       branch_address: Yup.string().required('Address is required.'),
    //       branch_time_zone: Yup.string().required('Timezone is required.'),
    //       is_hq: Yup.boolean()
    //     })
    //   )
    //   .test('at-least-one-branch', 'At least one branch is required.', function (value) {
    //     const { createError } = this;

    //     // Check if there is at least one branch with a value for branch_name
    //     const atLeastOneBranch = value?.length > 0;

    //     return atLeastOneBranch || createError({ path: 'branches', message: 'At least one branch is required.' });
    //   }),
    // departments: Yup.array()
    //   .of(
    //     Yup.object().shape({
    //       department_name: Yup.string().required('Department Name is required.')
    //     })
    //   )
    //   .test('at-least-one-department', 'At least one department is required.', function (value) {
    //     const { createError } = this;

    //     // Check if there is at least one branch with a value for branch_name
    //     const atLeastOneDepartment = value?.length > 0;

    //     return atLeastOneDepartment || createError({ path: 'departments', message: 'At least one department is required.' });
    //   }),
    // tenant_values: Yup.string().required('Value is required'),
    // tenant_mission: Yup.string().required('Mission is required'),
    // tenant_vision: Yup.string().required('Vision is required'),
    // tenant_playbook_values: Yup.array().of(
    //   Yup.object().shape({
    //     id: Yup.string(),
    //     tenant_values: Yup.string().required('Values is required.')
    //   })
    // ),
    company_logo: Yup.string().required('Company logo is required.'),
    company_thumbnail: Yup.string().required('Company thumbnail is required.'),
    primary_color: Yup.string().required('Primary color is required.'),
    secondary_color: Yup.string().required('Secondary color is required.'),
    email: Yup.string().email('Invalid email.').nullable(),
    group_of_companies: Yup.array().required('Group companies are required.').min(2, 'At least two group companies are required.')
    // subscription_plans: Yup.array()
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
    reset,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      is_demo_company: false,
      company_name: '',
      web_url: '',
      unique_url_component: '',
      email: '',
      fiscal_start_month: '',
      // update_frequency: '',
      // total_users: null,
      // branches: [
      //   {
      //     branch_name: '',
      //     branch_country: '',
      //     branch_state: '',
      //     branch_city: '',
      //     branch_address: '',
      //     branch_time_zone: '',
      //     isoCode: '',
      //     is_hq: false
      //   }
      // ],
      // departments: [{ department_name: '' }],
      // tenant_values: '',
      tenant_playbook_values: [
        {
          tenant_values: ''
        }
      ],
      tenant_mission: '',
      tenant_vision: '',
      playbook_id: '',
      theme_configuration_id: '',
      theme_id: '',
      company_logo: '',
      company_thumbnail: '',
      primary_color: '',
      secondary_color: '',
      group_of_companies: []
      // subscription_plans: []
    }
  });

  // const watchFiscalYear = watch('fiscal_start_month');
  // const watchUpdateFrequency = watch('update_frequency');
  // const watchDemoCompany = watch('is_demo_company');

  const onSubmit = async (values) => {
    try {
      setIsDirty(false);
      setIsSubmitting(true);
      let reqBody = {};
      if (values?.branches) {
        reqBody['branches'] = values.branches;
      }
      if (values?.departments) {
        reqBody['departments'] = values.departments;
      }
      reqBody['company'] = {
        company_name: values?.company_name,
        web_url: values?.web_url,
        unique_url_component: values?.unique_url_component,
        support_email: values.email,
        fiscal_start_month: values?.fiscal_start_month
        // user_limit: values?.total_users
      };
      reqBody['group_of_companies'] = values?.group_of_companies?.map((item) => item.id);
      reqBody['playbook'] = {
        id: values?.playbook_id,
        tenant_mission: values?.tenant_mission,
        tenant_playbook_values: values?.tenant_playbook_values?.filter((item) => item.tenant_values !== ''),
        // tenant_values: values?.tenant_values,
        tenant_vision: values?.tenant_vision
      };
      reqBody['update_frequency'] = values?.update_frequency;
      reqBody['theme_configuration'] = {
        id: values.theme_configuration_id,
        theme_id: values?.theme_id,
        tenant_theme_configuration_value: {
          company_logo: values?.company_logo,
          company_thumbnail: values?.company_thumbnail,
          primary_color: values?.primary_color,
          secondary_color: values?.secondary_color
        }
      };
      // reqBody['subscription_plans'] = values?.subscription_plans;
      // if (values?.playbook?.tenant_playbook_values) {
      //   reqBody['playbook'] = {
      //     ...values.playbook,
      //     tenant_playbook_values__: values.playbook?.tenant_playbook_values?.filter((item) => item.tenant_values !== '')
      //   };
      // }
      const result = await apiRequest.updateCompany(params?.id, reqBody);
      if (result?.data?.status === 200) {
        toast.success(result?.data?.message);
        navigate(-1);
      }
    } catch (error) {
      setIsDirty(true);
      if (error?.response?.data?.meta_data?.error_fields) {
        // toast.error(showErrors(error?.response?.data?.meta_data?.error_fields), {
        //   autoClose: 6000
        // });
      } else {
        toast.error(error?.response?.data?.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGroupCompanies = async () => {
    try {
      const response = await apiRequest.getGroupCompanies();
      const data = response?.data?.data;
      setGroupCompanies(data);
    } catch (error) {
      console.error('Error fetching group companies:', error);
    }
  };

  useEffect(() => {
    setIsDirty(true);
    if (params?.id) {
      fetchData(params?.id);
    }
    getGroupCompanies();
  }, [params]);

  const fetchData = async (id) => {
    try {
      const result = await apiRequest.companyDetails(id);
      const data = result?.data?.data;
      if (result?.data?.status === 200) {
        let stateData = {
          ...states
        };
        data?.branches?.map((item, index) => {
          const country = Country.getAllCountries().filter((_) => _.name === item.branch_country)?.[0];
          const stateList = State.getStatesOfCountry(country?.isoCode);
          stateData[index] = stateList;
        });
        setStates(stateData);
        reset({
          company_name: data?.company?.company_name,
          web_url: data?.company?.web_url,
          unique_url_component: data?.company?.unique_url_component,
          fiscal_start_month: data?.company?.fiscal_start_month,
          // update_frequency: data?.update_frequency ? data?.update_frequency : data?.existing_frequency_details?.update_frequency,
          // total_users: data?.total_users,
          // branches: data?.branches?.map((item) => ({
          //   ...item,
          //   branch_name: item.branch_name.replace('(headquarter)', '')?.trimRight()
          // })),
          // // demo company values
          // is_demo_company: data?.is_demo_company,
          // tenant_scale: data?.tenant_scale,

          // departments: data?.departments,
          // tenant_values: data?.playbook?.tenant_values,
          tenant_playbook_values: data?.playbook?.tenant_playbook_values,
          tenant_mission: data?.playbook?.tenant_mission,
          tenant_vision: data?.playbook?.tenant_vision,
          company_logo: data?.theme_configuration?.tenant_theme_configuration_value?.company_logo,
          company_thumbnail: data?.theme_configuration?.tenant_theme_configuration_value?.company_thumbnail,
          primary_color: data?.theme_configuration?.tenant_theme_configuration_value?.primary_color,
          secondary_color: data?.theme_configuration?.tenant_theme_configuration_value?.secondary_color,
          playbook_id: data?.playbook?.id,
          theme_configuration_id: data?.theme_configuration?.id,
          theme_id: data?.theme_configuration?.theme_id,
          email: data?.company?.support_email ?? '',
          child_company_id: data?.child_company_id,
          group_of_companies:
            data?.group_companies?.map((item) => {
              const id = item.id || item.tenant_id;
              return {
                ...item,
                id: id,
                tenant_id: id
              };
            }) || []
          // subscription_plans: data?.subscription_plans
        });

        setLogo(data?.theme_configuration?.company_logo);
        setThumbnail(data?.theme_configuration?.company_thumbnail);
        setPrimary(data?.theme_configuration?.tenant_theme_configuration_value?.primary_color);
        setSecondary(data?.theme_configuration?.tenant_theme_configuration_value?.secondary_color);
        setCompanyData(data);
      } else {
        setCompanyData(undefined);
      }
    } catch (error) {
      setCompanyData(undefined);
    }
  };

  const combinedCompanies = useMemo(() => {
    const normalizeCompanies = (companies) => {
      if (!companies) return [];
      if (!Array.isArray(companies)) {
        if (companies.tenant_groups && Array.isArray(companies.tenant_groups)) {
          return normalizeCompanies(companies.tenant_groups);
        }
        if (companies.company_name) {
          return [companies];
        }
        return [];
      }
      return companies
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const id = item.id || item.tenant_id;
          return {
            ...item,
            id: id,
            tenant_id: id
          };
        })
        .filter(Boolean);
    };

    const list = [...normalizeCompanies(groupCompanies), ...normalizeCompanies(companyData?.group_companies)];
    const uniqueMap = {};
    const result = [];
    for (const item of list) {
      if (item && item.id && !uniqueMap[item.id]) {
        uniqueMap[item.id] = true;
        result.push(item);
      }
    }
    return result;
  }, [groupCompanies, companyData?.group_companies]);

  usePrompt(isDirty);
  if (companyData === undefined) {
    return (
      <NotFound
        message={'Company not found!'}
        buttonTitle={'Go Back'}
        path="/companies"
        isCompany={true}
      />
    );
  }

  return (
    <MainCard
      content={false}
      sx={{ maxWidth: '728px', margin: 'auto', borderRadius: '16px', padding: { xs: '15px', md: '24px 48px 24px' } }}
    >
      <Box sx={{ mb: '24px' }}>
        <Typography
          textAlign={'center'}
          variant="h3"
          fontWeight={500}
        >
          Edit Company
        </Typography>
      </Box>
      <Box>
        <CustomTypography
          sx={{ fontWeight: 500, mb: '16px', opacity: 0.5 }}
          variant="body2"
          color={theme.palette.slate[900]}
          width={'160'}
        >
          Company Information
        </CustomTypography>
      </Box>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container>
          <CompanyInfo
            control={control}
            errors={errors}
            setValue={setValue}
            groupCompanies={combinedCompanies}
            clearErrors={clearErrors}
          />

          <Grid
            item
            xs={12}
          >
            <Box>
              <CustomTypography
                sx={{ fontWeight: 500, m: '8px 0px 24px', opacity: 0.5 }}
                variant="body2"
                color={theme.palette.slate[900]}
                width={'75'}
              >
                Playbook
              </CustomTypography>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Playbook
              control={control}
              errors={errors}
              isShowDemoCompany={false}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Box>
              <CustomTypography
                sx={{ fontWeight: 500, m: '15px 0px 15px', opacity: 0.5 }}
                variant="body2"
                color={theme.palette.slate[900]}
                width={'60'}
              >
                Theme
              </CustomTypography>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Theme
              control={control}
              errors={errors}
              primary={primary}
              setPrimary={setPrimary}
              secondary={secondary}
              setSecondary={setSecondary}
              logo={logo}
              setLogo={setLogo}
              thumbnail={thumbnail}
              setThumbnail={setThumbnail}
              setValue={setValue}
              setError={setError}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              sx={{ mt: '32px' }}
              spacing={1}
            >
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => navigate(-1)}
                sx={{ width: '200px', height: '40px' }}
              >
                Cancel
              </Button>
              <LoadingButton
                disableElevation
                loading={isSubmitting}
                loadingPosition="start"
                disabled={isSubmitting}
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                // onClick={handleNext}
                sx={{ width: '200px', height: '40px' }}
              >
                Save Changes
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </MainCard>
  );
};

export default GroupCompanyEdit;
